import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "./useApi";

// The org's full risks from the All Risks route (GET /allrisks/details), read only when a page first
// needs one - an expanded incident or CVE device on /soc - and kept until invalidated. `request()`
// starts the read; `riskFor(id)` is the RiskDetailsDto, undefined while loading (or before any
// request), and null when the read failed or the risk isn't in it. A failed read is retried by the
// next `request()` (the next row opened), so one transient failure doesn't last until the page is
// reloaded.
// `invalidate()` drops what was read (and any read in flight), so the next `request()` reads again; a
// new `resetKey` does the same (the SOC page passes the queue's changeKey, which moves after every
// write and on Refresh). `version` moves on each invalidation: a consumer with a row still open adds
// it to the effect that calls `request()`, so the open row re-reads at once.
export const useAllRiskDetails = ({ resetKey } = {}) => {
  const apiCall = useApi();
  // undefined: not asked yet; "loading"; a Map by id; null: the read failed.
  const [state, setState] = useState(undefined);
  const [version, setVersion] = useState(0);

  const request = useCallback(() => setState((s) => (s === undefined || s === null ? "loading" : s)), []);
  const invalidate = useCallback(() => {
    setState(undefined);
    setVersion((v) => v + 1);
  }, []);

  const lastKey = useRef(resetKey);
  useEffect(() => {
    if (lastKey.current === resetKey) return;
    lastKey.current = resetKey;
    invalidate();
  }, [resetKey, invalidate]);

  // Keyed on version too: an invalidation followed by a request in the same render leaves state at
  // "loading", and the read in flight from before must still be dropped and started again.
  useEffect(() => {
    if (state !== "loading") return undefined;
    let live = true;
    apiCall("GET", "/allrisks/details")
      .then((data) => { if (live) setState(Array.isArray(data) ? new Map(data.map((r) => [String(r.id), r])) : null); })
      .catch(() => { if (live) setState(null); });
    return () => { live = false; };
  }, [state, version, apiCall]);

  const riskFor = useCallback((id) => {
    if (state === undefined || state === "loading") return undefined;
    return state ? state.get(String(id)) ?? null : null;
  }, [state]);

  return { request, riskFor, invalidate, version };
};
